import styled, { css } from 'styled-components';
import SmallMarkdown from '../../Markdown/SmallMarkdown';
import { PSmall } from '../../elements/Text';
import { H1 } from '../../elements/Text';
import dayjs from 'dayjs';
import React from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from '../../Icons';

export const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 32px;
`;

export const Insight = ({
  value,
  title,
  icon,
  style,
  isDisabled,
  children,
  className,
}: {
  value: string;
  title: string;
  icon: string;
  style?: React.CSSProperties;
  isDisabled?: boolean;
  children?: React.ReactNode;
  className?: string;
}) => {
  if (!value) {
    return null;
  }
  return (
    <InsightWrapper isDisabled={isDisabled} style={style} className={className}>
      <InsightContent>
        <InsightTitle>
          <Icon className={icon} />
          {title}
        </InsightTitle>
        <MarkdownWrapper md={value ?? ''} />
      </InsightContent>

      {children}
    </InsightWrapper>
  );
};

const InsightContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
`;

const InsightWrapper = styled.div<{ isDisabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: auto;
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  padding: 24px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  position: relative;
  overflow: hidden;
  ${({ isDisabled }) =>
    isDisabled &&
    css`
      opacity: 0.5;
    `}
  justify-content: space-between;
`;
export const MarkdownWrapper = styled(SmallMarkdown)`
  th,
  td {
    font-size: 14px !important;
  }
`;

const InsightTitle = styled(PSmall)`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: 10px;
  letter-spacing: -0.2px;
`;

export const Icon = styled.i`
  font-size: 18px;
  font-weight: 500;
  padding: 0;
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  opacity: 0.9;
`;

export const Caption = styled.div`
  font-size: 13px;
  margin-top: 0;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textRegular};
  opacity: 0.7;
`;

export const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const Title = styled(H1)`
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.8px;
  line-height: 1.2;
`;

export const MetricLabel = styled.div<{
  isPrimary?: boolean;
}>`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textRegular};
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
  margin-bottom: 12px;
  opacity: 0.8;
  ${({ isPrimary }) =>
    isPrimary &&
    css`
      color: ${({ theme }) => theme.colors.textPrimary};
      opacity: 1;
    `}
`;

export const MetricValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.5px;
  line-height: 1.2;
`;

export const MetricCaption = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textRegular};
  opacity: 0.6;
`;

export const MetricSection = styled.div`
  padding: 8px;
`;

export const ProgressTrack = styled.div`
  height: 8px;
  margin: 12px 0 8px 0;
  width: 100%;
  background: ${({ theme }) => theme.colors.bgActive};
  border-radius: 999px;
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ width: number }>`
  height: 100%;
  width: ${({ width }) => width}%;
  background: ${({ theme }) => theme.colors.buttonPrimary};
  border-radius: 999px;
  transition: width 0.4s ease;
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const formatDuration = (seconds: number) => {
  return dayjs()
    .startOf('day')
    .second(seconds)
    .format(seconds > 3600 ? 'HH:mm:ss' : 'mm:ss');
};

export const computeTimeFromIntervals = (
  intervals: Array<{ startedAt: Date; endedAt: Date | null }> | undefined
): number => {
  if (!intervals || intervals.length === 0) {
    return 0;
  }
  return intervals.reduce((acc, interval) => {
    return (
      acc +
      dayjs(interval.endedAt ?? new Date()).diff(
        dayjs(interval.startedAt),
        'seconds'
      )
    );
  }, 0);
};

// Modal Components with Sticky Header and Liquid Glass Effect

const ModalUnderlay = styled.div`
  height: 100%;
  width: 100%;
  z-index: 3;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  animation: fadeIn 0.2s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 90vw;
  max-width: 800px;
  max-height: 85vh;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-radius: 12px;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s ease-out;
  -webkit-overflow-scrolling: touch;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.bgActive};
    border-radius: 12px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.textPlaceholder}60;
  }

  @keyframes slideIn {
    from {
      transform: scale(0.95) translateY(20px);
      opacity: 0;
    }
    to {
      transform: scale(1) translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    width: 95vw;
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div<{ isScrolled?: boolean }>`
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: end;
  padding: 24px 32px;
  background: ${({ theme }) => theme.colors.bgPage2}66;
  backdrop-filter: blur(25px) saturate(180%);
  -webkit-backdrop-filter: blur(25px) saturate(180%);
  border-bottom: 1px solid
    ${({ theme, isScrolled }) =>
      isScrolled ? theme.colors.bgActive : 'transparent'};
  transition: border-color 0.2s ease;

  /* Gradient mask effect for smooth blending */
  &::after {
    content: '';
    position: absolute;
    bottom: -32px;
    left: 0;
    right: 0;
    height: 32px;
    background: linear-gradient(
      to bottom,
      ${({ theme }) => theme.colors.bgPage2}99,
      ${({ theme }) => theme.colors.bgPage2}66,
      ${({ theme }) => theme.colors.bgPage2}33,
      transparent
    );
    pointer-events: none;
    backdrop-filter: blur(25px);
    -webkit-backdrop-filter: blur(25px);
  }

  @media (max-width: 768px) {
    padding: 20px 24px;
  }
`;

const ModalTitle = styled(H1)`
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.5px;
  margin-top: 36px;
`;

const ModalCloseButton = styled(CloseIcon)`
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s ease, transform 0.2s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalContent = styled.div`
  padding: 8px 32px 32px 32px;

  @media (max-width: 768px) {
    padding: 8px 24px 24px 24px;
  }
`;

interface StickyHeaderModalProps {
  title: string | React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
}

export const StickyHeaderModal: React.FC<StickyHeaderModalProps> = ({
  title,
  onClose,
  children,
}) => {
  const modalMountRef = React.useRef<HTMLDivElement | null>(null);
  const modalContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const modalRoot = document.getElementById('root');

  if (!modalMountRef.current) {
    modalMountRef.current = document.createElement('div');
  }

  React.useEffect(() => {
    if (modalRoot && modalMountRef.current) {
      modalRoot.prepend(modalMountRef.current);
    }
    return () => {
      if (modalRoot && modalMountRef.current) {
        modalRoot.removeChild(modalMountRef.current);
      }
    };
  }, [modalRoot]);

  // Handle escape key press
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Detect scroll to show/hide header border
  React.useEffect(() => {
    const container = modalContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setIsScrolled(container.scrollTop > 0);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  return createPortal(
    <ModalUnderlay onClick={onClose}>
      <ModalContainer
        ref={modalContainerRef}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader isScrolled={isScrolled}>
          <ModalTitle>{title}</ModalTitle>
          <ModalCloseButton onClick={onClose} />
        </ModalHeader>
        <ModalContent>{children}</ModalContent>
      </ModalContainer>
    </ModalUnderlay>,
    modalMountRef.current
  );
};

export const TooltipMarkdownContent = styled(SmallMarkdown)`
  margin-top: 8px;
  white-space: normal;
  * {
    font-size: 13px !important;
    color: ${({ theme }) => theme.colors.textRegular};
  }
`;

export const GradientScoreText = styled.span`
  color: ${({ theme }) => theme.colors.textActive};
  /* background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.textPrimary} 0%,
    ${({ theme }) => theme.colors.textPrimary} 50%,
    ${({ theme }) => theme.colors.buttonPrimary} 100%
  ); */
  /* -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text; */
  font-weight: 700;
`;

export const GradientConfidenceText = styled.span<{ confidence: number }>`
  color: ${({ theme }) => theme.colors.textActive};
  /* background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.textPrimary} 0%,
    ${({ theme }) => theme.colors.textPrimary} 50%,
    ${({ theme }) => theme.colors.buttonPrimary} 100%
  ); */
  /* -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text; */
  font-weight: 700;
`;

// Common score/confidence display components
export const ScoreValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`;

export const ScoreValueLarge = styled(GradientScoreText)`
  font-size: 28px;
  letter-spacing: -0.8px;
  line-height: 1;
`;

export const ScoreMax = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textRegular};
  opacity: 0.6;
`;

export const ConfidenceValueLarge = styled(GradientConfidenceText)`
  font-size: 20px;
  letter-spacing: -0.3px;
`;

// Modal Title Components - reusable across all modals
export const ModalTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ModalTitleMain = styled.div`
  font-size: 32px;
  margin-bottom: 0;
  font-weight: 700;
  letter-spacing: -0.8px;
  line-height: 1.2;
`;

export const ModalTitleCaption = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textRegular};
  font-weight: 400;
  line-height: 1.4;
`;

// Reusable Text Button Component
export const TextButton = styled.p<{
  active?: boolean;
  activeColor?: string;
  hoverColor?: string;
  disabled?: boolean;
  underlined?: boolean;
}>`
  margin: 0;
  padding: 0;
  font-size: 13px;
  font-weight: 500;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: color 0.3s ease-in-out, opacity 0.3s ease-in-out;
  opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
  text-decoration: ${({ underlined }) => (underlined ? 'underline' : 'none')};
  user-select: none;

  color: ${({ active, activeColor, disabled, theme }) => {
    if (disabled) return theme.colors.textPlaceholder;
    if (active && activeColor) return activeColor;
    return theme.colors.textPlaceholder;
  }};

  &:hover {
    color: ${({ hoverColor, activeColor, disabled, theme }) => {
      if (disabled) return theme.colors.textPlaceholder;
      if (hoverColor) return hoverColor;
      if (activeColor) return activeColor;
      return theme.colors.textActive;
    }};
  }
`;
